import React, { Fragment } from 'react';
import fetch from 'unfetch';
import chroma from 'chroma-js';
import { csvParse, autoType } from 'd3-dsv';

import { sortByKey } from '~/core/scripts/utils';
import { is768Query } from '~/core/scripts/mediaquery-helper';
import Icon from '~/core/scripts/components/Icon';
import Utils from '~/core/scripts/utils';

const isSmallScreen = !is768Query();

export async function loadData({ dataSource, sortColumn, sortDirection }) {
  const response = await fetch(dataSource);
  const csv = await response.text();

  let data = csvParse(csv, autoType).map((d, i) => ({ ...d, key: i }));

  if (sortColumn) {
    data.sort(sortByKey(sortColumn, sortDirection === 'asc'));
  }

  data = data.map((d, i) => ({ ...d, __rank: i + 1 }));

  return data;
}

export function getColorScale({ rowBgColorSteps }) {
  if (!rowBgColorSteps) {
    return false;
  }

  const colors = rowBgColorSteps.slice(0, rowBgColorSteps.length - 1).map(step => step[1]);
  const classes = rowBgColorSteps.map(step => step[0]);

  return chroma.scale(colors).classes(classes);
}

export function parseColumns({ columns, showRank, rankColor }) {
  return columns.map((column, index) => {
    const render = getRenderFunc({ column, showRank, rankColor, index });

    return {
      ...column,
      title: addIcon(parseColumnOption(column.title), column),
      dataIndex: parseColumnOption(column.dataIndex),
      render
    };
  });
}

function addIcon(title, column){
  if (!column.icon) return title;
  switch (column.icon) {
    case "house": return (
      <span><Icon type="house" /> {title}</span>
    ); break;
    case "wage": return (
      <span><Icon type="wage" /> {title}</span>
    ); break;
  }
}

function getRenderFunc({ column, index, showRank, rankColor }) {
  if (index === 0 && showRank) {
    return renderRankCell(rankColor);
  }

  if (column.bar) {
    return renderBarCell(column);
  }
  
  if (column.percentage) {
    return renderPercentage(column);
  }
  
  if (column.percentagebar) {
    return renderPercentageBar(column);
  }
  
  return (value) => parseValue(value);
}

// helper for parsing title=mobileTitle|desktopTitle depeding on the current screen size
function parseColumnOption(value) {
  const valueString = value.toString();

  if (!valueString.includes('|')) {
    return value;
  }

  return valueString.split('|')[isSmallScreen ? 0 : 1];
}

function renderPercentage(column) {
  return (value, row, index) => {
    return Utils.numberFormat(value, 1).replace(/^([0-9])/,'+$1')+"%";
  };
}

function renderPercentageBar(column) {
  return (value, row, index) => (
    <div className="table__cell-bar-container">
      <div className="table__cell-bar-value">{(Utils.numberFormat(value, 1).replace(/^([0-9])/,'+$1')+"%")}</div>
      <div className="table__cell-bar">
        <div
          className="table__cell-barvis"
          style={{ width: `${(Math.abs(value)*4.329004329)}%`, background: `${( column.rankColor )}` || '#555' }}
        />
      </div>
    </div>
  );
}

function renderBarCell(column) {
  return (value, row, index) => (
    <div className="table__cell-bar">
      <div
        className="table__cell-barvis"
        style={{ width: `${value * 100}%`, background: column.barColor || '#555' }}
      />
    </div>
  );
}


var lastDif = null;
var showRank = true;

function renderRankCell(rankColor) {
  return (value, row, index) => {
    showRank = (lastDif !== row.dif);
    lastDif = row.dif;
    return (
    <div className="table__cell-rank">
      {showRank && (
        <div className="table__cell-rank-index" style={{ background: rankColor }}>
        {row.__rank}
        </div>
      )}
      {!showRank && (
        <div className="table__cell-rank-index"></div>
      )}
      <div className="table__cell-rank-value">
        <div className="table__cell-rank-value-top">
          {parseValue(row.gen)}
        </div>
        <div className="table__cell-rank-value-bottom">
          {parseValue(row.bundesland)}
        </div>
      </div>
    </div>
  );
  }
}

function parseValue(value) {
  if (typeof value === 'undefined') {
    return null;
  }

  const valueStr = value.toString();
  const iconMatch = valueStr.match(/{icon:(.+)}/);

  if (iconMatch) {
    return (
      <span dangerouslySetInnerHTML={{
        __html: valueStr.replace(/{icon:(.+)}/, `<svg class="icon"><use xlink:href="spritemap.svg#icon-${iconMatch[1]}"></use></svg>`)
      }} />
    );
  }

  return value;
}
